import { Checkbox, useAppContext } from "@ftdr/blueprint-components-react";
import { payment3_paymentmethodpb } from "@ftdr/payment3_paymentmethod_coordinator-js-client";
import { BraintreePayPalButtons } from "@paypal/react-paypal-js";
import classNames from "classnames";
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { usePaymentContext } from "../../../../../contexts/payment-context";
import {
  usePaymentsClientContext,
  usePaymentsClientStateContext,
} from "../../../../../contexts/payments-client-context";
import {
  setPaypalError,
  setIsPending,
} from "../../../../../contexts/payments-client-context/state";
import {
  GeneralStatusValue,
  RegistrationResponse,
  SuccessTypeValue,
} from "../../../../../types";
import {
  AgregatedProcessPaymentResponse,
  FailureTypeValue,
  PaymentProcessorClientInternalRef,
  PaypalProcessorComponents,
  ProcessPaymentPaypalRequestData,
} from "../../../types";
import { useHandleCreatePaymentMethod } from "./hooks";
import {
  disbursementBraintreeButtonStyles,
  paymentBraintreeButtonStyles,
} from "./paypal-braintree-buttons.styles";
import { usePayPalBraintreeButtons } from "./use-paypal-braintree-buttons";
import { getSaveInWallet } from "../../../../../utils";

export type PayPalBraintreeButtonsProps = {
  components: Omit<
    PaypalProcessorComponents,
    "ErrorBoundaryComponent" | "PaypalProcessorScriptProviderView"
  >;
};

export const PayPalBraintreeButtons = forwardRef<
  Pick<
    PaymentProcessorClientInternalRef<ProcessPaymentPaypalRequestData>,
    "processPayment"
  >,
  PayPalBraintreeButtonsProps
>(({ components }, ref) => {
  const {
    isButtonsInitializing,
    setIsButtonsInitializing,
    handleCreateBillingAgreement,
    handleOnApproveAction,
    handleOnError,
  } = usePayPalBraintreeButtons();

  const [saveInWalletChecked, setSaveInWalletChecked] = useState<
    boolean | undefined
  >(undefined);

  const createPaymentMethodResultRef = useRef<
    AgregatedProcessPaymentResponse | undefined
  >(undefined);

  const handleCreatePaymentMethod = useHandleCreatePaymentMethod();
  const { state } = usePaymentsClientStateContext();
  const {
    createPaymentMethodRegistration,
    dispatch,
    cashFlow,
    applicationState,
  } = usePaymentsClientContext();
  const {
    appSettings: { localizedText },
  } = useAppContext();

  const {
    formsSetup,
    state: { isSetAsDefaultChecked, isSaveInWalletChecked },
    setWalletCheckboxProps,
    updateDefaultPaymentMethod,
  } = usePaymentContext();
  const saveInWalletSetup =
    formsSetup?.braintreeForm?.fieldsSetup?.saveInWallet;
  const brand = applicationState?.formsSetup?.braintreeForm?.brand;

  useImperativeHandle(ref, () => ({
    processPayment: async (requestData) => {
      dispatch(setIsPending(true));
      if (state.paypalData?.braintreeTokenizePayloadData?.nonce) {
        if (
          createPaymentMethodResultRef?.current?.paypal?.type ===
          SuccessTypeValue.PAYPAL_SUCCESS
        ) {
          dispatch(setIsPending(false));
          return createPaymentMethodResultRef?.current;
        }

        createPaymentMethodResultRef.current = await handleCreatePaymentMethod(
          {
            ...requestData,
            ...{
              paypal: {
                ...requestData?.paypal,
                ...{
                  includedInWallet: getSaveInWallet(
                    isSetAsDefaultChecked,
                    isSaveInWalletChecked,
                    !!requestData?.paypal?.includedInWallet ||
                      saveInWalletChecked
                  ),
                },
              },
            },
          },
          state?.clientSetup?.registration?.registrationID || ""
        );

        let registrationResult: RegistrationResponse | undefined = undefined;

        switch (createPaymentMethodResultRef?.current?.paypal?.type) {
          case FailureTypeValue.REDIS_CACHE_404_ERROR:
            registrationResult = await createPaymentMethodRegistration({
              paymentMethod: {
                details: {
                  type: payment3_paymentmethodpb.PaymentMethodType.PayPal,
                  cashFlow,
                },
                ...(brand && { brand }),
              },
            });

            createPaymentMethodResultRef.current = await handleCreatePaymentMethod(
              requestData,
              registrationResult?.registration?.registrationID || ""
            );
        }
      } else {
        const errorMessage = localizedText(`PAYPAL_PAYMENT_PROCESS_REQUIRED`);
        dispatch(
          setPaypalError({
            isError: true,
            message: errorMessage,
          })
        );

        createPaymentMethodResultRef.current = {
          paypal: {
            type: FailureTypeValue.NO_FILLED_PAYPAL_INFO,
            paypalFailureResponse: {
              message: errorMessage,
            },
          },
          generalStatus: GeneralStatusValue.PAYMENT_FAILED,
        };
      }

      if (
        createPaymentMethodResultRef?.current.generalStatus ===
          GeneralStatusValue.PAYMENT_SUCCESS &&
        isSetAsDefaultChecked
      ) {
        const setAsDefaultResponse = await updateDefaultPaymentMethod({
          contractID: setWalletCheckboxProps?.contractID,
          customerID: setWalletCheckboxProps?.customerID,
          dataStore: {
            id:
              createPaymentMethodResultRef?.current?.paypal
                ?.paypalSuccessResponse?.paymentMethod?.paymentMethodID,
            dataLocation: 3,
          },
          propertyID: setWalletCheckboxProps?.propertyID,
          sourceTypeCode: setWalletCheckboxProps?.sourceTypeCode || "CSC",
        });

        dispatch(setIsPending(false));
        return {
          ...createPaymentMethodResultRef?.current,
          ...(setAsDefaultResponse?.status && {
            generalStatus: setAsDefaultResponse.status,
          }),
          setAsDefault: setAsDefaultResponse,
        };
      } else {
        dispatch(setIsPending(false));
        return createPaymentMethodResultRef?.current;
      }
    },
  }));
  return (
    <>
      {isButtonsInitializing && (
        <components.LoadingSpinner
          styleType={applicationState?.styleType || "default"}
        />
      )}
      {state?.paypalError?.message && (
        <components.ErrorNotification
          message={state.paypalError.message}
          className={"w-full mb-6"}
          data-testid="paypal-error-notification"
        />
      )}
      {state.isPending && (
        <div className="w-full h-full absolute top-0 left-0 flex justify-center items-center z-10">
          <components.LoadingSpinner
            styleType={applicationState?.styleType || "default"}
          />
        </div>
      )}
      {state.isPaypalApproved ? (
        <components.PayPalApprovedNotification data-testid="paypal-approved-notification" />
      ) : (
        <>
          <BraintreePayPalButtons
            onInit={() => {
              setIsButtonsInitializing(false);
            }}
            className={classNames("BraintreeFormRow", {
              hidden: isButtonsInitializing,
            })}
            style={
              cashFlow
                ? disbursementBraintreeButtonStyles
                : paymentBraintreeButtonStyles
            }
            createBillingAgreement={handleCreateBillingAgreement}
            onApprove={handleOnApproveAction}
            onError={handleOnError}
          />
        </>
      )}
      {saveInWalletSetup && !isButtonsInitializing && (
        <div
          className="grid grid-cols-6 mt-2 BraintreeFormRow"
          data-testid="BraintreeSaveInWalletRow"
        >
          <Checkbox
            checked={saveInWalletChecked}
            onChange={() => setSaveInWalletChecked(!saveInWalletChecked)}
            label={saveInWalletSetup?.label}
            className={saveInWalletSetup?.className}
          />
        </div>
      )}
    </>
  );
});

PayPalBraintreeButtons.displayName = "BraintreeButtons";
